import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Badge } from '@truecare/ui';

/**
 * Reusable persona-agnostic /me workspace shell (layout "C").
 *
 * Structure: page title → identity header (name · persona chip · status) →
 * optional banner (shown on every tab) → ONE horizontal, mobile-scrollable tab
 * strip of flat working tabs → active-tab content.
 *
 * Only the caregiver branch of /me wires this today; facility + homecare adopt
 * it next by supplying their own `tabs` config — the shell is identical, only
 * the tab set differs. Keep it persona-agnostic: no caregiver-specific logic
 * belongs here.
 */

export interface PersonaWorkspaceTab {
  key: string;
  label: string;
  /** Rendered only while active (each tab owns its own queries). */
  render: () => ReactNode;
}

export interface PersonaWorkspaceProps {
  identity: {
    name: string;
    personaLabel: string;
    statusBadge?: ReactNode;
  };
  /** Optional slot under the identity header, visible on every tab. */
  banner?: ReactNode;
  tabs: PersonaWorkspaceTab[];
  defaultTabKey: string;
}

export function PersonaWorkspace({
  identity,
  banner,
  tabs,
  defaultTabKey,
}: PersonaWorkspaceProps) {
  const [activeKey, setActiveKey] = useState(() =>
    tabs.some((t) => t.key === defaultTabKey)
      ? defaultTabKey
      : (tabs[0]?.key ?? ''),
  );
  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];

  // Stable per-tab ids so the tab ↔ panel relationship is exposed to assistive
  // tech. Keys are simple slugs (offers/schedule/…); sanitize defensively.
  const slug = (key: string) => key.replace(/[^a-zA-Z0-9_-]/g, '-');
  const tabId = (key: string) => `pw-tab-${slug(key)}`;
  const panelId = (key: string) => `pw-panel-${slug(key)}`;

  // Roving-tabindex keyboard nav (WAI-ARIA tabs pattern): Arrow keys move
  // selection between tabs, Home/End jump to the ends. Mirrors the marketing
  // faq.astro tabs implementation.
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  function onTabKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const count = tabs.length;
    if (count === 0) return;
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (index + 1) % count;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (index - 1 + count) % count;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = count - 1;
    else return;
    e.preventDefault();
    const nextTab = tabs[next];
    if (!nextTab) return;
    setActiveKey(nextTab.key);
    tabRefs.current[next]?.focus();
  }

  return (
    <div>
      {/* The person's name IS the page heading — this is their workspace, not a
          "profile" page (it holds offers, shifts, payouts, etc.). Persona +
          status sit beside it. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="font-heading text-3xl md:text-4xl text-forest">
          {identity.name}
        </h1>
        <Badge tone="forest">{identity.personaLabel}</Badge>
        {identity.statusBadge}
      </div>

      {banner && <div className="mt-4 flex flex-col gap-4">{banner}</div>}

      {/* Section nav — a native dropdown on phones (a horizontal tab strip does
          not collapse gracefully once there are 6+ tabs), a tab strip on ≥sm. */}
      <div className="mt-6">
        <label className="sr-only" htmlFor="workspace-section">
          {identity.personaLabel} section
        </label>
        <select
          id="workspace-section"
          className="w-full rounded-md border border-ink-soft bg-paper px-3 py-2 font-body text-sm text-ink sm:hidden"
          value={active?.key}
          onChange={(e) => setActiveKey(e.target.value)}
        >
          {tabs.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>

        <div
          role="tablist"
          aria-label={`${identity.personaLabel} sections`}
          className="hidden gap-1 overflow-x-auto whitespace-nowrap border-b border-ink-soft sm:flex"
        >
          {tabs.map((t, i) => {
            const isActive = t.key === active?.key;
            return (
              <button
                key={t.key}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={tabId(t.key)}
                aria-selected={isActive}
                aria-controls={panelId(t.key)}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveKey(t.key)}
                onKeyDown={(e) => onTabKeyDown(e, i)}
                className={
                  'shrink-0 px-3 py-2 font-body text-sm rounded-md ' +
                  (isActive
                    ? 'bg-forest text-paper'
                    : 'text-ink-muted hover:text-forest')
                }
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="mt-6 focus:outline-none"
        role="tabpanel"
        id={active ? panelId(active.key) : undefined}
        aria-labelledby={active ? tabId(active.key) : undefined}
        tabIndex={0}
      >
        {active?.render()}
      </div>
    </div>
  );
}
